/**
 * SmartActionBuilder — visual Trigger → Filters → Actions pipeline editor.
 *
 * Three-column layout:
 *   1. Trigger      — pick from 7 trigger types (left rail of cards)
 *   2. Filters      — add as many (field, op, value) rules as you want.
 *                     Enum fields (game/region/retailer/product_type/rarity)
 *                     show dropdowns so you can't typo "Pokémon" vs "Pokemon".
 *   3. Actions      — chain of actions to run on each match.
 */
import React from "react";
import {
  Plus, Trash2, Copy, Play, Radio, ShoppingCart, MessageCircle, Send,
  Star, ShoppingBag, MousePointerClick, Terminal, RotateCcw,
  ListChecks, Clock, TrendingDown,
} from "lucide-react";

const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]";
const SELECT = INPUT;
const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

// --- Triggers --------------------------------------------------------------
// Each entry: value (= backend Literal), label, icon, description, enabled
const TRIGGERS = [
  { value: "in_stock", label: "In Stock NOW", icon: ShoppingBag, color: "#22C55E",
    desc: "🔥 Fires every scan a matching listing IS in stock (eager — no transition required). Use this for 'buy any available stock immediately'. Combine with max_total_spend so it stops at your budget." },
  { value: "product_monitor", label: "Product Monitor", icon: Radio, color: "#A855F7",
    desc: "Fires when the scanner detects a NEW listing (any state) on any retailer (~15 sec cadence)." },
  { value: "restock", label: "Restock Detection", icon: RotateCcw, color: "#10B981",
    desc: "Fires only when a previously out-of-stock product flips back to in-stock. Conservative — won't double-buy." },
  { value: "price_drop", label: "Price Drop", icon: TrendingDown, color: "#EAB308",
    desc: "Fires when a watched listing's price drops below the threshold in your filters." },
  { value: "scheduled", label: "Scheduled", icon: Clock, color: "#3B82F6",
    desc: "Fires on a recurring schedule (set the cron in the trigger config). Useful for daily restock sweeps." },
  { value: "manual_api", label: "Manual / API", icon: Terminal, color: "#A3A3A3",
    desc: "Fires only when you POST to /api/scalp/profiles/{id}/fire — useful for testing or chaining from other tools." },
  { value: "task_result", label: "Task Result", icon: ListChecks, color: "#EC4899",
    desc: "Fires when ANOTHER Smart Action's auto-buy finishes (success or fail). Useful for chaining notifications." },
  { value: "checkout_feed", label: "Checkout Feed", icon: ShoppingBag, color: "#06B6D4",
    desc: "Fires every time one of YOUR auto-buys completes checkout — useful as a logging or backup-purchase rail." },
];

// --- Filter fields --------------------------------------------------------
// type: "string" | "number" | "bool" | "enum"
// options (for enum): provided live by /api/reference via the parent
const FIELDS = [
  { value: "title", label: "Title", type: "string" },
  { value: "game", label: "Game", type: "enum", refKey: "games" },
  { value: "set_name", label: "Set name", type: "enum-multi-source", refKey: "sets_by_game" },
  { value: "product_type", label: "Product type", type: "enum", refKey: "product_types" },
  { value: "rarity", label: "Rarity", type: "enum", refKey: "rarities" },
  { value: "retailer_id", label: "Retailer", type: "enum", refKey: "retailers", refValueField: "id", refLabelField: "name" },
  { value: "region", label: "Region", type: "enum", refKey: "regions" },
  { value: "price", label: "Price ($)", type: "number" },
  { value: "deal_score", label: "Deal score (0-100)", type: "number" },
  { value: "stock_count", label: "Stock count", type: "number" },
  { value: "is_real", label: "Real listing (vs demo)", type: "bool" },
];

const OPS_BY_TYPE = {
  string: ["equals", "not_equals", "contains", "not_contains", "in", "not_in"],
  number: ["equals", "lt", "lte", "gt", "gte"],
  bool: ["is_true"],
  enum: ["equals", "not_equals", "in", "not_in"],
  "enum-multi-source": ["equals", "not_equals", "in", "not_in"],
};

const ACTION_TYPES = [
  { value: "auto_buy", label: "Auto-buy", icon: ShoppingCart, color: "#EF4444", hint: "Run the local Playwright adapter to log in → add to cart → autofill checkout → place order. Only fires in the .exe build." },
  { value: "notify_discord", label: "Notify Discord", icon: MessageCircle, color: "#5865F2", hint: "Send a ping to the Discord webhook configured in Settings. Works in cloud + .exe." },
  { value: "notify_telegram", label: "Notify Telegram", icon: Send, color: "#0EA5E9", hint: "Same as Discord but on Telegram." },
  { value: "add_to_watchlist", label: "Save match", icon: Star, color: "#EAB308", hint: "Just log the match — no purchase, no notification. Useful for tracking restocks without scalping." },
];

function fieldMeta(fieldValue) {
  return FIELDS.find((f) => f.value === fieldValue) || FIELDS[0];
}

function fieldType(fieldValue) {
  return fieldMeta(fieldValue).type;
}

// Returns the list of {value, label} options for an enum field, drawn live
// from /api/reference so it can never drift out of sync with the backend.
function optionsFor(field, reference, otherFilters) {
  const meta = fieldMeta(field);
  if (meta.type === "enum") {
    const raw = reference?.[meta.refKey] || [];
    if (meta.refValueField) {
      return raw.map((r) => ({ value: r[meta.refValueField], label: r[meta.refLabelField] || r[meta.refValueField] }));
    }
    return raw.map((v) => ({ value: v, label: v }));
  }
  if (meta.type === "enum-multi-source") {
    // set_name depends on the chosen game.  Look at other filters to pick the
    // right game-keyed bucket; fall back to "Pokemon".
    const gameFilter = (otherFilters || []).find((f) => f.field === "game");
    const game = (gameFilter && gameFilter.value) || "Pokemon";
    const all = (reference?.[meta.refKey] || {})[game] || [];
    return all.map((v) => ({ value: v, label: v }));
  }
  return [];
}


function FilterValueInput({ rule, fieldT, options, onChange, testId }) {
  if (fieldT === "bool" || rule.op === "is_true") return null;
  const isList = rule.op === "in" || rule.op === "not_in";
  // Free-text combo for set_name (so brand-new sets that aren't in the dropdown
  // can still be typed in). Applies to both single-value and list ops.
  const isFreeTextField = rule.field === "set_name";
  if (fieldT === "enum" || fieldT === "enum-multi-source") {
    if (isList) {
      const current = Array.isArray(rule.value) ? rule.value : [];
      // For set_name list-mode, render checkboxes for known sets PLUS an
      // input that lets the user add custom set names as comma-separated chips.
      if (isFreeTextField) {
        const customs = current.filter((v) => !options.find((o) => o.value === v));
        const [draft, setDraft] = [{ get: () => "", set: () => {} }, {}]; // not used
        return (
          <div className="bg-[#050505] border border-[#262626] rounded-sm p-2 max-h-[220px] overflow-y-auto space-y-2">
            <input
              data-testid={`${testId}-custom-add`}
              type="text"
              placeholder="Type a custom set + press Enter…"
              className="w-full bg-[#0a0a0a] border border-[#1f1f1f] text-white text-[12px] px-2 py-1.5 rounded-sm focus:outline-none focus:border-[#EAB308]"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = e.target.value.trim();
                  if (v && !current.includes(v)) onChange({ ...rule, value: [...current, v] });
                  e.target.value = "";
                }
              }}
            />
            {customs.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {customs.map((c) => (
                  <span key={c} className="text-[10px] font-mono bg-[#EAB308]/15 text-[#EAB308] border border-[#EAB308]/40 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                    {c}
                    <button type="button" onClick={() => onChange({ ...rule, value: current.filter((v) => v !== c) })}
                            className="hover:text-white">×</button>
                  </span>
                ))}
              </div>
            )}
            {options.map((opt) => {
              const checked = current.includes(opt.value);
              return (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-[12px] text-white hover:text-[#EAB308]">
                  <input
                    data-testid={`${testId}-${opt.value}`}
                    type="checkbox"
                    className="accent-[#EAB308]"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? current.filter((v) => v !== opt.value) : [...current, opt.value];
                      onChange({ ...rule, value: next });
                    }}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        );
      }
      // Default (non-free-text) list mode
      return (
        <div className="bg-[#050505] border border-[#262626] rounded-sm p-2 max-h-[180px] overflow-y-auto space-y-1">
          {options.length === 0 && <div className="text-[10px] text-[#525252] px-1">No options available</div>}
          {options.map((opt) => {
            const checked = current.includes(opt.value);
            return (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-[12px] text-white hover:text-[#EAB308]">
                <input
                  data-testid={`${testId}-${opt.value}`}
                  type="checkbox"
                  className="accent-[#EAB308]"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? current.filter((v) => v !== opt.value) : [...current, opt.value];
                    onChange({ ...rule, value: next });
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    // Single-value: free-text combo via datalist for set_name; regular select otherwise.
    if (isFreeTextField) {
      const listId = `${testId}-options`;
      return (
        <>
          <input
            data-testid={testId}
            list={listId}
            className={SELECT}
            value={rule.value ?? ""}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
            placeholder="Type any set name, or pick from list"
            autoComplete="off"
          />
          <datalist id={listId}>
            {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </datalist>
        </>
      );
    }
    return (
      <select
        data-testid={testId}
        className={SELECT}
        value={rule.value ?? ""}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
      >
        <option value="">— pick —</option>
        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    );
  }
  if (fieldT === "number") {
    return (
      <input
        data-testid={testId}
        className={INPUT}
        type="number"
        step="any"
        placeholder="0"
        value={rule.value ?? ""}
        onChange={(e) => onChange({ ...rule, value: e.target.value === "" ? "" : Number(e.target.value) })}
      />
    );
  }
  // string
  if (isList) {
    return (
      <input
        data-testid={testId}
        className={INPUT}
        placeholder="comma,separated,values"
        value={Array.isArray(rule.value) ? rule.value.join(",") : ""}
        onChange={(e) => onChange({ ...rule, value: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
      />
    );
  }
  return (
    <input
      data-testid={testId}
      className={INPUT}
      placeholder="value"
      value={rule.value ?? ""}
      onChange={(e) => onChange({ ...rule, value: e.target.value })}
    />
  );
}


function FilterRow({ index, rule, onChange, onDuplicate, onRemove, reference, allFilters }) {
  const ft = fieldType(rule.field);
  const ops = OPS_BY_TYPE[ft];
  const options = optionsFor(rule.field, reference, allFilters);
  return (
    <div
      data-testid={`filter-row-${index}`}
      className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm p-3 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#525252]">{index === 0 ? "CONTINUE IF" : "AND IF"}</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`filter-duplicate-${index}`}
            onClick={onDuplicate}
            title="Duplicate this filter"
            className="text-[#525252] hover:text-[#EAB308] transition-colors"
          ><Copy className="w-3.5 h-3.5" /></button>
          <button
            type="button"
            data-testid={`filter-remove-${index}`}
            onClick={onRemove}
            title="Remove this filter"
            className="text-[#525252] hover:text-[#EF4444] transition-colors"
          ><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select
          data-testid={`filter-field-${index}`}
          className={SELECT}
          value={rule.field}
          onChange={(e) => {
            const nf = e.target.value;
            const nt = fieldType(nf);
            const op = OPS_BY_TYPE[nt].includes(rule.op) ? rule.op : OPS_BY_TYPE[nt][0];
            onChange({ ...rule, field: nf, op, value: nt === "bool" ? true : "" });
          }}
        >
          {FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select
          data-testid={`filter-op-${index}`}
          className={SELECT}
          value={rule.op}
          onChange={(e) => onChange({ ...rule, op: e.target.value })}
        >
          {ops.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <FilterValueInput
        rule={rule}
        fieldT={ft}
        options={options}
        onChange={onChange}
        testId={`filter-value-${index}`}
      />
    </div>
  );
}


function ActionAutoBuy({ form, setForm, accounts, payments, proxies, shippingProfiles = [] }) {
  const mode = form.checkout_mode || "account";
  return (
    <div className="space-y-2">
      <div className="text-[10px] text-[#737373] leading-relaxed">
        Logs in (or guest), adds the item to cart, autofills the saved card &amp; address, and submits the order. Only fires when the <code className="text-[#EAB308]">.exe</code> is running.
      </div>

      {/* Checkout mode toggle */}
      <div className="flex gap-1.5 p-1 bg-[#050505] border border-[#262626] rounded-sm">
        <button
          type="button"
          data-testid="checkout-mode-account"
          onClick={() => setForm({ ...form, checkout_mode: "account" })}
          className={"flex-1 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-sm " + (mode === "account" ? "bg-[#EAB308] text-black" : "text-[#A3A3A3] hover:text-white")}
        >
          Use store account
        </button>
        <button
          type="button"
          data-testid="checkout-mode-guest"
          onClick={() => setForm({ ...form, checkout_mode: "guest" })}
          className={"flex-1 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-sm " + (mode === "guest" ? "bg-[#22C55E] text-black" : "text-[#A3A3A3] hover:text-white")}
        >
          Guest checkout
        </button>
      </div>

      {mode === "account" ? (
        <div>
          <div className={LABEL}>Store account</div>
          <select
            data-testid="action-buy-account"
            className={SELECT}
            value={form.store_account_id || ""}
            onChange={(e) => setForm({ ...form, store_account_id: e.target.value })}
          >
            <option value="">— pick a retailer login —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.retailer_id} · {a.username}</option>)}
          </select>
        </div>
      ) : (
        <div>
          <div className={LABEL}>Shipping profile</div>
          <select
            data-testid="action-buy-shipping"
            className={SELECT}
            value={form.shipping_profile_id || ""}
            onChange={(e) => setForm({ ...form, shipping_profile_id: e.target.value })}
          >
            <option value="">— use default —</option>
            {shippingProfiles.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label} · {s.first_name} {s.last_name} · {s.city}, {s.region}
              </option>
            ))}
          </select>
          {shippingProfiles.length === 0 && (
            <div className="mt-1.5 text-[10px] text-[#FBBF24]">
              ⚠ No shipping profiles yet — add one in <a href="/app/shipping" className="underline">/app/shipping</a> first.
            </div>
          )}
        </div>
      )}

      <div>
        <div className={LABEL}>Payment method *</div>
        <select
          data-testid="action-buy-payment"
          className={SELECT}
          value={form.payment_method_id || ""}
          onChange={(e) => setForm({ ...form, payment_method_id: e.target.value })}
        >
          <option value="">— pick a card —</option>
          {payments.map((p) => <option key={p.id} value={p.id}>{p.brand} •••• {p.last4}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className={LABEL}>Qty</div>
          <input
            data-testid="action-buy-qty"
            type="number" min="1" max="10"
            className={INPUT}
            value={form.quantity || 1}
            onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 1 })}
          />
        </div>
        <div className="col-span-2">
          <div className={LABEL}>Proxy strategy</div>
          <select
            data-testid="action-buy-proxy-strategy"
            className={SELECT}
            value={form.proxy_strategy}
            onChange={(e) => setForm({ ...form, proxy_strategy: e.target.value })}
          >
            <option value="none">No proxy</option>
            <option value="any">Any active proxy</option>
            <option value="rotate_by_region">Rotate by region</option>
            <option value="specific">Specific proxy</option>
          </select>
        </div>
      </div>
      {form.proxy_strategy === "specific" && (
        <div>
          <div className={LABEL}>Pick proxy</div>
          <select
            data-testid="action-buy-proxy"
            className={SELECT}
            value={form.proxy_id || ""}
            onChange={(e) => setForm({ ...form, proxy_id: e.target.value })}
          >
            <option value="">— pick —</option>
            {proxies.filter((p) => p.active).map((p) => <option key={p.id} value={p.id}>{p.label || `${p.host}:${p.port}`}</option>)}
          </select>
        </div>
      )}
      <div>
        <div className={LABEL}>Max total spend ($)</div>
        <input
          data-testid="action-buy-max-spend"
          type="number" step="0.01" min="0"
          className={INPUT}
          placeholder="optional cap across all attempts"
          value={form.max_total_spend ?? ""}
          onChange={(e) => setForm({ ...form, max_total_spend: e.target.value === "" ? "" : Number(e.target.value) })}
        />
      </div>
      <label className="flex items-start gap-2 cursor-pointer mt-1">
        <input
          data-testid="action-buy-allow-preorder"
          type="checkbox"
          checked={!!form.allow_preorder}
          onChange={(e) => setForm({ ...form, allow_preorder: e.target.checked })}
          className="mt-0.5 accent-[#EAB308]"
        />
        <span className="text-[11px] leading-snug">
          <span className="font-bold text-white">Allow preorder items</span>
          <span className="block text-[#737373] text-[10px] mt-0.5">
            Some retailers (JB Hi-Fi, BIG W) sell upcoming releases with a "Preorder" button instead of "Add to cart".
            By default the bot SKIPS these. Tick this to let the bot place preorders too.
          </span>
        </span>
      </label>
    </div>
  );
}
function ActionRow({ index, action, onChange, onRemove, form, setForm, accounts, payments, proxies, shippingProfiles }) {
  const meta = ACTION_TYPES.find((t) => t.value === action.type) || ACTION_TYPES[0];
  const Icon = meta.icon;
  return (
    <div
      data-testid={`action-row-${index}`}
      className="bg-[#0a0a0a] border rounded-sm p-3 space-y-2"
      style={{ borderColor: `${meta.color}33` }}
    >
      <div className="flex items-center justify-between gap-2">
        <select
          data-testid={`action-type-${index}`}
          className={SELECT + " flex-1 font-bold"}
          value={action.type}
          onChange={(e) => onChange({ ...action, type: e.target.value, config: {} })}
        >
          {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button
          type="button"
          data-testid={`action-remove-${index}`}
          onClick={onRemove}
          className="text-[#525252] hover:text-[#EF4444]"
        ><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex items-start gap-2 text-[10px] text-[#737373] leading-relaxed">
        <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: meta.color }} />
        <span>{meta.hint}</span>
      </div>
      {action.type === "auto_buy" && (
        <ActionAutoBuy form={form} setForm={setForm} accounts={accounts} payments={payments} proxies={proxies} shippingProfiles={shippingProfiles} />
      )}
      {(action.type === "notify_discord" || action.type === "notify_telegram") && (
        <div className="text-[11px] text-[#525252]">
          Destination configured globally in <a href="/app/settings" className="text-[#EAB308] hover:underline">Settings</a>.
        </div>
      )}
    </div>
  );
}


function TriggerCard({ trigger, active, onPick }) {
  const Icon = trigger.icon;
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={`trigger-card-${trigger.value}`}
      className="text-left w-full px-3 py-2.5 rounded-sm border transition-all flex items-start gap-2.5"
      style={{
        background: active ? `${trigger.color}10` : "#0a0a0a",
        borderColor: active ? `${trigger.color}aa` : "#1f1f1f",
        boxShadow: active ? `0 0 12px ${trigger.color}33` : "none",
      }}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: trigger.color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold text-white">{trigger.label}</div>
        <div className="text-[10px] text-[#A3A3A3] leading-snug mt-0.5">{trigger.desc}</div>
      </div>
    </button>
  );
}


function WatchedUrlsRow({ form, setForm }) {
  const [open, setOpen] = React.useState(() => Array.isArray(form.watched_urls) && form.watched_urls.length > 0);
  const value = Array.isArray(form.watched_urls) ? form.watched_urls.join("\n") : "";
  const count = (form.watched_urls || []).filter(Boolean).length;
  return (
    <div className="border-b border-[#262626] bg-[#0a0a0a]" data-testid="watched-urls-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-[#0d0d0d] transition-colors"
        data-testid="watched-urls-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-[#3B82F6]">
            Direct URL Watch
          </span>
          <span className="text-[10px] font-mono text-[#525252]">
            {count > 0 ? `${count} URL${count === 1 ? "" : "s"} pinned · scanner polls each every cycle` : "optional · paste exact product URLs to monitor"}
          </span>
        </div>
        <span className="text-[#737373] text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="px-5 pb-4 -mt-1">
          <div className="text-[11px] text-[#A3A3A3] leading-relaxed mb-2 max-w-3xl">
            For products you already know about, paste the full PDP URL (one per line). The scanner polls each
            URL directly every cycle (~20s) — far more reliable than search-page discovery (no Akamai blocks, ~10×
            less Bright Data bandwidth) — and auto-fires the moment that exact URL shows in-stock.
          </div>
          <div className="text-[10px] text-[#525252] leading-relaxed mb-2 max-w-3xl bg-[#0d0d0d] border border-[#1a1a1a] rounded-sm p-2">
            <strong className="text-[#22C55E]">Auto-pin tip:</strong> when a Bright Data Scraper IDE collector
            (Settings → Bright Data Web Scraper IDE) finds a record that matches this profile&apos;s filters,
            its URL is automatically appended here — so broad-discovery and instant-snipe stay in sync.
          </div>
          <textarea
            data-testid="watched-urls-textarea"
            className="w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252] font-mono min-h-[110px] resize-y"
            placeholder={"https://www.bigw.com.au/product/.../p/123456\nhttps://www.target.com.au/p/.../A-789\nhttps://www.jbhifi.com.au/products/..."}
            value={value}
            onChange={(e) => {
              const urls = e.target.value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
              setForm({ ...form, watched_urls: urls });
            }}
            spellCheck={false}
          />
          <div className="text-[10px] text-[#525252] mt-1">
            Recognised hosts: bigw.com.au · target.com.au · kmart.com.au · jbhifi.com.au · ebgames.com.au · pokemoncenter.com · tcgplayer.com · target.com
          </div>
        </div>
      )}
    </div>
  );
}


export default function SmartActionBuilder({
  form, setForm, onSubmit, onCancel,
  accounts = [], payments = [], proxies = [], shippingProfiles = [],
  reference = {},
  saving,
  submitLabel,
}) {
  const filters = form.filters || [];
  const actions = form.actions || [];
  const activeTrigger = TRIGGERS.find((t) => t.value === form.trigger) || TRIGGERS[0];

  const setFilter = (i, rule) => setForm({ ...form, filters: filters.map((f, idx) => idx === i ? rule : f) });
  const addFilter = () => setForm({ ...form, filters: [...filters, { field: "game", op: "equals", value: "Pokemon" }] });
  const removeFilter = (i) => setForm({ ...form, filters: filters.filter((_, idx) => idx !== i) });
  const duplicateFilter = (i) => {
    const orig = filters[i];
    if (!orig) return;
    // Clone deep-enough — filters only carry primitives + arrays
    const copy = { ...orig, value: Array.isArray(orig.value) ? [...orig.value] : orig.value };
    delete copy.id; // let the backend assign a new id on save
    setForm({ ...form, filters: [...filters.slice(0, i + 1), copy, ...filters.slice(i + 1)] });
  };

  const setAction = (i, action) => setForm({ ...form, actions: actions.map((a, idx) => idx === i ? action : a) });
  const addAction = () => setForm({ ...form, actions: [...actions, { type: "notify_discord", config: {} }] });
  const removeAction = (i) => setForm({ ...form, actions: actions.filter((_, idx) => idx !== i) });

  const hasAutoBuy = actions.some((a) => a.type === "auto_buy");
  const complete = !!form.name && filters.length > 0 && actions.length > 0;

  return (
    <form
      data-testid="smart-action-builder"
      onSubmit={onSubmit}
      className="bg-[#0d0d0d] border border-[#262626] rounded-sm"
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626] bg-[#0a0a0a]">
        <div className="flex items-center gap-3 flex-1">
          <input
            data-testid="scalp-name-input"
            className="bg-transparent text-white font-heading text-lg font-bold uppercase tracking-wider focus:outline-none placeholder:text-[#525252] flex-1"
            placeholder="Smart Action name (e.g. Pokemon ETB scout)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono"
               style={{ color: complete ? "#22C55E" : "#737373" }}>
            {complete ? "Ready" : `${filters.length} filters · ${actions.length} actions`}
          </div>
        </div>
        <div className="flex gap-2 ml-3">
          {onCancel && (
            <button
              type="button"
              data-testid="scalp-cancel-btn"
              onClick={onCancel}
              className="px-4 py-2 border border-[#262626] text-[#A3A3A3] uppercase tracking-wider text-xs font-bold rounded-sm hover:bg-[#1A1A1A]"
            >Cancel</button>
          )}
          <button
            data-testid="scalp-save-btn"
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-[#EF4444] text-white uppercase tracking-wider text-xs font-bold rounded-sm hover:bg-[#F87171] disabled:opacity-40 flex items-center gap-1.5"
          >
            <Play className="w-3.5 h-3.5" />
            {submitLabel || "Save smart action"}
          </button>
        </div>
      </div>

      {/* Direct URL Watch — power-user feature, collapsed by default */}
      <WatchedUrlsRow form={form} setForm={setForm} />

      {/* 3-column pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-[#262626]">
        {/* TRIGGER COLUMN */}
        <div className="p-5" data-testid="builder-col-trigger">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-sm bg-[#A855F7]/20 border border-[#A855F7]/40 flex items-center justify-center font-mono text-xs text-[#A855F7] font-bold">1</div>
            <div className="font-heading text-sm font-bold uppercase tracking-wider">Trigger</div>
            <div className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-[#A855F7]">{activeTrigger.value}</div>
          </div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
            {TRIGGERS.map((t) => (
              <TriggerCard
                key={t.value}
                trigger={t}
                active={t.value === form.trigger}
                onPick={() => setForm({ ...form, trigger: t.value })}
              />
            ))}
          </div>
        </div>

        {/* FILTERS COLUMN */}
        <div className="p-5" data-testid="builder-col-filters">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-sm bg-[#EAB308]/20 border border-[#EAB308]/40 flex items-center justify-center font-mono text-xs text-[#EAB308] font-bold">2</div>
            <div className="font-heading text-sm font-bold uppercase tracking-wider">Filters</div>
            <div className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-[#EAB308]">all must match</div>
          </div>
          <button
            type="button"
            data-testid="add-filter-btn"
            onClick={addFilter}
            className="w-full py-2.5 mb-2 bg-[#EAB308]/10 border border-dashed border-[#EAB308]/40 text-[#EAB308] uppercase tracking-wider text-[11px] font-bold rounded-sm hover:bg-[#EAB308]/20 flex items-center justify-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add filter
          </button>
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {filters.length === 0 ? (
              <div className="text-[11px] text-[#525252] text-center py-4 leading-relaxed">
                No filters → matches <strong>every</strong> new listing.<br/>
                Add at least one to narrow it down.
              </div>
            ) : (
              filters.map((f, i) => (
                <FilterRow
                  key={f.id || `f-${i}`}
                  index={i}
                  rule={f}
                  onChange={(r) => setFilter(i, r)}
                  onDuplicate={() => duplicateFilter(i)}
                  onRemove={() => removeFilter(i)}
                  reference={reference}
                  allFilters={filters}
                />
              ))
            )}
          </div>
        </div>

        {/* ACTIONS COLUMN */}
        <div className="p-5" data-testid="builder-col-actions">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-sm bg-[#22C55E]/20 border border-[#22C55E]/40 flex items-center justify-center font-mono text-xs text-[#22C55E] font-bold">3</div>
            <div className="font-heading text-sm font-bold uppercase tracking-wider">Actions</div>
            <div className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-[#22C55E]">run in order</div>
          </div>
          <button
            type="button"
            data-testid="add-action-btn"
            onClick={addAction}
            className="w-full py-2.5 mb-2 bg-[#22C55E]/10 border border-dashed border-[#22C55E]/40 text-[#22C55E] uppercase tracking-wider text-[11px] font-bold rounded-sm hover:bg-[#22C55E]/20 flex items-center justify-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add action
          </button>
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {actions.length === 0 ? (
              <div className="text-[11px] text-[#525252] text-center py-4 leading-relaxed">
                No actions → matches will be silently dropped.<br/>
                Add at least one (try <strong>Auto-buy</strong> or <strong>Notify Discord</strong>).
              </div>
            ) : (
              actions.map((a, i) => (
                <ActionRow
                  key={a.id || `a-${i}`}
                  index={i}
                  action={a}
                  onChange={(r) => setAction(i, r)}
                  onRemove={() => removeAction(i)}
                  form={form} setForm={setForm}
                  accounts={accounts} payments={payments} proxies={proxies}
                  shippingProfiles={shippingProfiles}
                />
              ))
            )}
          </div>
          {hasAutoBuy && !form.payment_method_id && (
            <div className="mt-2 text-[10px] text-[#FBBF24] leading-relaxed">
              ⚠ Pick a payment method inside the Auto-buy card or this profile won&apos;t arm.
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
